import { makeAutoObservable, toJS, runInAction, computed } from "mobx";
import RootStore from "stores/RootStore";
// import databaseDate from "shared/common/date/databaseDate";
import { getDateRange } from "@intrasync/utils";
// import ScheduleInterface from "./AIAgent.ScheduleInterface";
import ScheduleInterfaceLeftHeaderCell from "./ScheduleInterface/ScheduleInterfaceCells/ScheduleInterface.LeftHeaderCell";
import ScheduleInterfaceLeftBodyCell from "./ScheduleInterface/ScheduleInterfaceCells/ScheduleInterface.LeftBodyCell";
import ServiceStore from "stores/ServiceStore";
import ScheduleInterfaceRightBodyCell from "./ScheduleInterface/ScheduleInterfaceCells/ScheduleInterface.RightBodyCell";
import ScheduleInterfaceRightHeaderCell from "./ScheduleInterface/ScheduleInterfaceCells/ScheduleInterface.RightHeaderCell";
import type ConversationStore from "stores/ConversationStore";

// Chat Interface Types
export interface ConversationThread {
  id: number;
  title: string;
  description?: string;
  messageCount: number;
  lastMessageAt: string;
  isEphemeral: boolean;
  status: "active" | "archived";
  userId: number;
  sharedWith?: number[];
  createdAt: string;
  updatedAt: string;
}

export interface ThreadCreateData {
  title?: string;
  description?: string;
  isEphemeral?: boolean;
}

export interface ThreadUpdateData {
  title?: string;
  description?: string;
  status?: "active" | "archived";
  isEphemeral?: boolean;
}

interface ChatMessage {
  id?: number | string;
  userId?: number;
  userMessage?: string;
  assistantMessage?: string;
  userMessageCreatedAt?: string;
  assistantMessageCreatedAt?: string;
  searchResultIds?: string[];
  searchResultsByService?: SearchResultsByService;
  rating?: 'positive' | 'negative' | 'neutral' | null;
  regeneratedFromId?: number;
  channelId?: number | string;
  entities?: Panel[];
  isLoading?: boolean;
  error?: string;
  threadId?: number;
}

interface SearchResultsByService {
  [serviceName: string]: string[];
}

interface ChatMessagePatch {
  rating?: 'positive' | 'negative' | 'neutral' | null;
  channelId?: number;
  threadId?: number;
}

interface ServiceEntities {
  [serviceName: string]: {
    ids: string[];
    entities: Array<Record<string, unknown>>;
    totalCount?: number;
  };
}

interface Message {
  content: string | {
    id: number;
    content: string;
    message: string;
    searchResultIds: string[];
  };
  role: "user" | "system";
  timestamp?: number;
  id?: string;
  thumbsUp?: boolean;
  thumbsDown?: boolean;
  searchResultIds?: string[];
  searchResultsByService?: SearchResultsByService;
  entities?: Panel[];
  serviceEntities?: ServiceEntities;
  chatMessageId?: number;
  state?: 'sending' | 'waiting' | 'complete' | 'error' | 'regenerating';
}

interface Panel {
  id: number;
  [key: string]: unknown;
}

const BASE_SCHEDULE_PARAMETERS = {
  jobNumbers: [],
  startDate: "",
  endDate: "",
  leadTimeWeeks: "8",
  dailySqFtLimit: "1000", // Default daily square footage limit
  includeWeekends: false,
  includeHolidays: false,
  naturalLanguageQuery: "", // Natural language query for AI agent
  optimizationGoal: "balanced", // Default optimization goal
  enableSmartScheduling: true, // Enable smart scheduling by default
  useHistoricalData: true, // Use historical data by default
  maxIterations: "100", // Default maximum iterations for AI agent
  sortMetric: "Installation Sequence", // Default limit metric
};

class AIAgentStore {
  rootStore: RootStore;

  // Schedule Parameters state
  jobNumbers: string[] = BASE_SCHEDULE_PARAMETERS.jobNumbers;
  startDate: string = BASE_SCHEDULE_PARAMETERS.startDate;
  endDate: string = BASE_SCHEDULE_PARAMETERS.endDate;
  leadTimeWeeks: string = BASE_SCHEDULE_PARAMETERS.leadTimeWeeks;
  dailySqFtLimit: string = BASE_SCHEDULE_PARAMETERS.dailySqFtLimit;
  includeWeekends: boolean = BASE_SCHEDULE_PARAMETERS.includeWeekends;
  includeHolidays: boolean = BASE_SCHEDULE_PARAMETERS.includeHolidays;
  naturalLanguageQuery: string = BASE_SCHEDULE_PARAMETERS.naturalLanguageQuery;

  // AI Agent configuration state
  optimizationGoal: string = BASE_SCHEDULE_PARAMETERS.optimizationGoal;
  enableSmartScheduling: boolean =
    BASE_SCHEDULE_PARAMETERS.enableSmartScheduling;
  useHistoricalData: boolean = BASE_SCHEDULE_PARAMETERS.useHistoricalData;
  maxIterations: string = BASE_SCHEDULE_PARAMETERS.maxIterations;
  sortMetric: string = BASE_SCHEDULE_PARAMETERS.sortMetric;

  // Chat Interface state
  userInput = "";
  selectedPreset = "None";
  messages: Message[] = [
    {
      content: "Welcome to IntraSync! How can I help you today?",
      role: "system",
      timestamp: Date.now(),
    },
  ];
  loading = false;
  channelId: string | null = null;
  private socketListenersSetup = false;
  private threadStore: ServiceStore | null = null;
  
  // Dialog state
  showSaveDialog = false;
  showNewThreadWarning = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { 
      rootStore: false,
      conversationStore: computed
    } as any);
    // Access the conversation-threads store
    this.threadStore = rootStore.conversationThreadsStore as ServiceStore;
    // Setup socket listeners after a delay to ensure client is ready
    setTimeout(() => this.setupSocketListeners(), 100);
  }

  resetScheduleParameters = () => {
    this.jobNumbers = BASE_SCHEDULE_PARAMETERS.jobNumbers;
    this.startDate = BASE_SCHEDULE_PARAMETERS.startDate;
    this.endDate = BASE_SCHEDULE_PARAMETERS.endDate;
    this.leadTimeWeeks = BASE_SCHEDULE_PARAMETERS.leadTimeWeeks;
    this.dailySqFtLimit = BASE_SCHEDULE_PARAMETERS.dailySqFtLimit;
    this.includeWeekends = BASE_SCHEDULE_PARAMETERS.includeWeekends;
    this.includeHolidays = BASE_SCHEDULE_PARAMETERS.includeHolidays;
    this.naturalLanguageQuery = BASE_SCHEDULE_PARAMETERS.naturalLanguageQuery;
    this.optimizationGoal = BASE_SCHEDULE_PARAMETERS.optimizationGoal;
    this.enableSmartScheduling = BASE_SCHEDULE_PARAMETERS.enableSmartScheduling;
    this.useHistoricalData = BASE_SCHEDULE_PARAMETERS.useHistoricalData;
    this.maxIterations = BASE_SCHEDULE_PARAMETERS.maxIterations;
    this.sortMetric = BASE_SCHEDULE_PARAMETERS.sortMetric;
  };

  // State setters
  setJobNumbers = (value: unknown) => {
    console.log("setJobNumbers", value);
    if (Array.isArray(value)) {
      this.jobNumbers = value;
    }
    // this.jobNumbers = value ? [value] : null;
  };

  setStartDate = (value: string) => {
    this.startDate = value;
  };

  setEndDate = (value: string) => {
    this.endDate = value;
  };

  setLeadTimeWeeks = (value: string) => {
    this.leadTimeWeeks = value;
  };

  setDailySqFtLimit = (value: string) => {
    this.dailySqFtLimit = value;
  };

  setIncludeWeekends = (value: boolean) => {
    this.includeWeekends = value;
  };

  setIncludeHolidays = (value: boolean) => {
    this.includeHolidays = value;
  };

  setNaturalLanguageQuery = (value: string) => {
    this.naturalLanguageQuery = value;
  };

  setOptimizationGoal = (value: string) => {
    this.optimizationGoal = value;
  };

  setEnableSmartScheduling = (value: boolean) => {
    this.enableSmartScheduling = value;
  };

  setUseHistoricalData = (value: boolean) => {
    this.useHistoricalData = value;
  };

  setMaxIterations = (value: string) => {
    this.maxIterations = value;
  };

  setLimitMetric = (value: string) => {
    this.sortMetric = value;
  };

  // Chat Interface Methods
  get conversationStore(): ConversationStore {
    return this.rootStore.conversationStore as ConversationStore;
  }

  // Delegated getters for thread state
  get currentThread() {
    return this.conversationStore?.currentThread;
  }

  get threads() {
    return this.conversationStore?.threads || [];
  }

  get threadsLoading() {
    return this.conversationStore?.isThreadsLoading || false;
  }

  setUserInput(value: string) {
    this.userInput = value;
  }

  setSelectedPreset(value: string) {
    this.selectedPreset = value;
  }

  addMessage(message: Message) {
    if (!message.id) {
      message.id = `msg_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 11)}`;
    }
    this.messages.push(message);
  }

  setLoading(value: boolean) {
    this.loading = value;
  }

  setShowSaveDialog(value: boolean) {
    this.showSaveDialog = value;
  }

  setShowNewThreadWarning(value: boolean) {
    this.showNewThreadWarning = value;
  }

  resetChat() {
    // Clear messages in UI
    this.messages = [
      {
        content: "Welcome to IntraSync! How can I help you today?",
        role: "system",
        timestamp: Date.now(),
      },
    ];
    this.userInput = "";
    this.loading = false;
  }

  // Thread management methods delegated to ConversationStore
  async loadThreads() {
    await this.conversationStore?.loadThreads();
    return this.conversationStore?.threads || [];
  }

  async selectThread(threadId: number) {
    // Load thread and set as current
    await this.conversationStore?.loadThreads();
    const threads = this.conversationStore?.threads || [];
    const thread = threads.find((t: any) => t.id === threadId);
    if (thread) {
      this.conversationStore?.setCurrentThread(thread);
      await this.conversationStore?.loadMessages(threadId);
    }
  }

  async createNewThread(isEphemeral = false) {
    return this.conversationStore?.createThread({ isEphemeral });
  }

  async saveCurrentThread(title: string, description?: string) {
    const currentThread = this.currentThread;
    if (currentThread) {
      return this.conversationStore?.saveThread(currentThread.id, title, description);
    }
  }

  async archiveCurrentThread() {
    const currentThread = this.currentThread;
    if (currentThread) {
      return this.conversationStore?.archiveThread(currentThread.id);
    }
  }

  async deleteCurrentThread() {
    const currentThread = this.currentThread;
    if (currentThread) {
      return this.conversationStore?.deleteThread(currentThread.id);
    }
  }

  checkForUnsavedChanges() {
    // Check if current thread has unsaved changes
    const currentThread = this.currentThread;
    if (!currentThread) return false;
    
    // A thread is considered to have unsaved changes if it's ephemeral and has messages
    return currentThread.isEphemeral && currentThread.messageCount > 0;
  }

  handleNewThreadRequest() {
    if (this.checkForUnsavedChanges()) {
      this.setShowNewThreadWarning(true);
    } else {
      this.createNewThread(true);
    }
  }

  async handleSubmit() {
    if (this.userInput.trim() === "") return;

    this.setLoading(true);
    const userMessageText = this.userInput;
    
    const tempUserMessage: Message = {
      content: userMessageText,
      role: "user",
      timestamp: Date.now(),
      id: `temp-${Date.now()}`,
      state: 'sending',
    };
    this.addMessage(tempUserMessage);
    this.setUserInput("");

    const tempAssistantId = `temp-assistant-${Date.now()}`;
    const tempAssistantMessage: Message = {
      content: "Thinking...",
      role: "system",
      timestamp: Date.now(),
      id: tempAssistantId,
      state: 'waiting',
    };
    this.addMessage(tempAssistantMessage);

    try {
      if (userMessageText.toLowerCase().includes("help")) {
        const helpContent = `I can help you query panel and production information. Here are some examples:

**Panel Casting Queries:**
- "What panels cast tomorrow?"
- "What panels cast on [specific date]?"
- "What panels cast between two dates?"
- "Mark numbers of panels cast in a date range"

**Production Analytics:**
- "What % of panels are complete for a specific job?"
- "Total number of panels cast over past 7 days"
- "What panels have specific hardware?"

**Usage Tips:**
- Use the preset questions button for common queries
- Specify date ranges for production schedules
- Include job numbers when asking about completion percentages

Try asking about panel casting details or production statistics for a specific time period.`;
        
        runInAction(() => {
          const assistantMsg = this.messages.find(m => m.id === tempAssistantId);
          if (assistantMsg) {
            assistantMsg.content = helpContent;
            assistantMsg.state = 'complete';
          }
        });
        this.setLoading(false);
        return;
      }

      const chatMessagesStore = this.rootStore.chatMessagesStore as ServiceStore;
      
      if (!this.currentThread) {
        await this.createNewThread(true);
      }
      
      const chatMessage = await chatMessagesStore.create({
        userMessage: userMessageText,
        threadId: this.currentThread?.id,
        channelId: this.channelId ? parseInt(this.channelId, 10) : undefined,
        generateResponse: true,
      }) as ChatMessage;
      
      if (this.currentThread) {
        runInAction(() => {
          if (this.currentThread) {
            this.currentThread.messageCount += 1;
            this.currentThread.lastMessageAt = new Date().toISOString();
          }
        });
      }

      if (!chatMessage) {
        throw new Error("Failed to create message");
      }

      runInAction(() => {
        const userMsg = this.messages.find(m => m.id === tempUserMessage.id);
        if (userMsg) {
          userMsg.chatMessageId = chatMessage.id as number;
          userMsg.id = `user-${chatMessage.id}`;
          userMsg.state = 'complete';
        }

        const assistantMsg = this.messages.find(m => m.id === tempAssistantId);
        if (assistantMsg) {
          assistantMsg.content = chatMessage.assistantMessage || "Error generating response. Please try again.";
          assistantMsg.chatMessageId = chatMessage.id as number;
          assistantMsg.id = `assistant-${chatMessage.id}`;
          assistantMsg.searchResultIds = chatMessage.searchResultIds;
          assistantMsg.searchResultsByService = chatMessage.searchResultsByService;
          assistantMsg.state = 'complete';
        }
      });

      if (chatMessage.searchResultsByService || chatMessage.searchResultIds) {
        await this.fetchServiceEntities(
          chatMessage,
          `assistant-${chatMessage.id}`
        );
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      runInAction(() => {
        const assistantMsg = this.messages.find(m => m.id === tempAssistantId);
        if (assistantMsg) {
          assistantMsg.content = "Sorry, I encountered an error. Please try again.";
          assistantMsg.state = 'error';
        }
      });
    } finally {
      this.setLoading(false);
    }
  }

  async fetchServiceEntities(chatMessage: ChatMessage, messageId: string) {
    const serviceEntities: ServiceEntities = {};
    
    if (chatMessage.searchResultsByService) {
      for (const [serviceName, ids] of Object.entries(chatMessage.searchResultsByService)) {
        if (ids && ids.length > 0) {
          try {
            const store = this.rootStore[`${serviceName}Store`] as ServiceStore;
            if (store) {
              await store.find({ query: { id: { $in: ids } } });
              const entities = ids.map(id => 
                store.entities.find((e: any) => e.id === id || e[`${serviceName}Id`] === id)
              ).filter(Boolean);
              
              serviceEntities[serviceName] = {
                ids,
                entities,
                totalCount: entities.length
              };
            }
          } catch (error) {
            console.error(`Failed to fetch ${serviceName} entities:`, error);
          }
        }
      }
    } else if (chatMessage.searchResultIds && chatMessage.searchResultIds.length > 0) {
      const panelStore = this.rootStore.panelStore as ServiceStore;
      if (panelStore) {
        await panelStore.find({ query: { id: { $in: chatMessage.searchResultIds } } });
        const entities = chatMessage.searchResultIds.map(id => 
          panelStore.entities.find((e: any) => e.id === id || e.panelId === id)
        ).filter(Boolean);
        
        serviceEntities.panel = {
          ids: chatMessage.searchResultIds,
          entities,
          totalCount: entities.length
        };
      }
    }

    runInAction(() => {
      const message = this.messages.find(m => m.id === messageId);
      if (message) {
        message.serviceEntities = serviceEntities;
      }
    });
  }

  async updateMessageRating(messageId: number, rating: 'positive' | 'negative' | 'neutral' | null) {
    try {
      const chatMessagesStore = this.rootStore.chatMessagesStore as ServiceStore;
      const patchData: ChatMessagePatch = { rating };
      
      await chatMessagesStore.patch(messageId, patchData);
      
      runInAction(() => {
        const message = this.messages.find(m => 
          m.chatMessageId === messageId || 
          m.id === `assistant-${messageId}` ||
          m.id === `user-${messageId}`
        );
        if (message) {
          if (rating === 'positive') {
            message.thumbsUp = true;
            message.thumbsDown = false;
          } else if (rating === 'negative') {
            message.thumbsUp = false;
            message.thumbsDown = true;
          } else {
            message.thumbsUp = false;
            message.thumbsDown = false;
          }
        }
      });
    } catch (error) {
      console.error("Failed to update message rating:", error);
    }
  }

  setupSocketListeners() {
    if (this.socketListenersSetup) return;
    
    const app = this.rootStore.app;
    if (!app || !app.io) {
      setTimeout(() => this.setupSocketListeners(), 100);
      return;
    }

    const socket = app.io;
    
    socket.on('user-typing', (data: any) => {
      console.log('User typing:', data);
    });

    socket.on('assistant-response-start', (data: any) => {
      runInAction(() => {
        const message = this.messages.find(m => m.chatMessageId === data.messageId);
        if (message) {
          message.state = 'waiting';
        }
      });
    });

    socket.on('assistant-response-chunk', (data: any) => {
      runInAction(() => {
        const message = this.messages.find(m => m.chatMessageId === data.messageId);
        if (message) {
          if (typeof message.content === 'string') {
            message.content = data.chunk;
          }
          message.state = 'waiting';
        }
      });
    });

    socket.on('assistant-response-complete', (data: any) => {
      runInAction(() => {
        const message = this.messages.find(m => m.chatMessageId === data.messageId);
        if (message) {
          if (typeof message.content === 'string') {
            message.content = data.fullResponse;
          }
          message.state = 'complete';
        }
      });
    });

    this.socketListenersSetup = true;
  }

  getMessageEntities(messageId: string): Panel[] {
    const message = this.messages.find((msg) => msg.id === messageId);
    console.log("getMessageEntities message", message);
    
    // First check legacy entities
    if (message?.entities) {
      return message.entities;
    }
    
    // Then check new serviceEntities structure
    if (message?.serviceEntities) {
      // Flatten all service entities into a single array
      const allEntities: Panel[] = [];
      for (const [serviceName, serviceData] of Object.entries(message.serviceEntities)) {
        if (serviceData.entities) {
          allEntities.push(...(serviceData.entities as Panel[]));
        }
      }
      return allEntities;
    }
    
    return [];
  }

  async setThumbsUp(messageId: string) {
    const message = this.messages.find((msg) => msg.id === messageId);
    if (!message || !message.chatMessageId) return;

    const rating = message.thumbsUp ? null : 'positive';
    await this.updateMessageRating(message.chatMessageId, rating as any);
  }

  async setThumbsDown(messageId: string) {
    const message = this.messages.find((msg) => msg.id === messageId);
    if (!message || !message.chatMessageId) return;

    const rating = message.thumbsDown ? null : 'negative';
    await this.updateMessageRating(message.chatMessageId, rating as any);
  }

  async generateThreadTitle(): Promise<string | null> {
    const currentThread = this.conversationStore?.currentThread || this.currentThread;
    if (!currentThread) return null;
    
    try {
      const title = await this.conversationStore.generateTitle(currentThread.id);
      return title;
    } catch (error) {
      console.error("Failed to generate thread title:", error);
      return null;
    }
  }

  async regenerateResponse(messageId: string) {
    const messageIndex = this.messages.findIndex((msg) => msg.id === messageId);
    if (messageIndex === -1) return;

    const originalMessage = this.messages[messageIndex];
    const userMessageIndex = messageIndex - 1;
    
    if (userMessageIndex < 0 || this.messages[userMessageIndex].role !== 'user') {
      console.error('Could not find corresponding user message');
      return;
    }

    const userMessage = this.messages[userMessageIndex];
    const userText = typeof userMessage.content === 'string' 
      ? userMessage.content 
      : userMessage.content.message;

    // Mark the current message as regenerating
    runInAction(() => {
      originalMessage.state = 'regenerating';
      originalMessage.content = 'Regenerating response...';
    });

    try {
      const chatMessagesStore = this.rootStore.chatMessagesStore as ServiceStore;
      
      const newChatMessage = await chatMessagesStore.create({
        userMessage: userText,
        threadId: this.currentThread?.id,
        channelId: this.channelId ? parseInt(this.channelId, 10) : undefined,
        generateResponse: true,
        regeneratedFromId: originalMessage.chatMessageId,
      }) as ChatMessage;

      if (!newChatMessage) {
        throw new Error("Failed to regenerate message");
      }

      runInAction(() => {
        originalMessage.content = newChatMessage.assistantMessage || "Error regenerating response.";
        originalMessage.chatMessageId = newChatMessage.id as number;
        originalMessage.id = `assistant-${newChatMessage.id}`;
        originalMessage.searchResultIds = newChatMessage.searchResultIds;
        originalMessage.searchResultsByService = newChatMessage.searchResultsByService;
        originalMessage.state = 'complete';
        originalMessage.thumbsUp = false;
        originalMessage.thumbsDown = false;
      });

      if (newChatMessage.searchResultsByService || newChatMessage.searchResultIds) {
        await this.fetchServiceEntities(
          newChatMessage,
          `assistant-${newChatMessage.id}`
        );
      }
    } catch (error) {
      console.error("Failed to regenerate response:", error);
      runInAction(() => {
        originalMessage.content = "Failed to regenerate response. Please try again.";
        originalMessage.state = 'error';
      });
    }
  }

  // Method to generate schedule
  generateSchedule = async () => {
    // Collect all parameters into a single object
    const schedulingParameters = {
      // Job configuration
      jobNumbers: Array.isArray(this.jobNumbers)
        ? toJS(this.jobNumbers)
        : [toJS(this.jobNumbers)],
      startDate: this.startDate,
      endDate: this.endDate,
      leadTimeWeeks: parseInt(this.leadTimeWeeks) || 8,
      dailySqFtLimit: this.dailySqFtLimit
        ? parseInt(this.dailySqFtLimit)
        : null,
      includeWeekends: this.includeWeekends,
      includeHolidays: this.includeHolidays,
      naturalLanguageQuery: this.naturalLanguageQuery,

      // AI agent configuration
      optimizationGoal: this.optimizationGoal,
      enableSmartScheduling: this.enableSmartScheduling,
      useHistoricalData: this.useHistoricalData,
      maxIterations: parseInt(this.maxIterations) || 100,
    };

    // You would then send this object to your API
    // For now, just log it
    console.log("Scheduling parameters:", schedulingParameters);
    // This would connect to the backend scheduling service
    console.log("Generating schedule with AI agent");
    try {
      const scheduleGeneratorStore = this.rootStore
        .scheduleGeneratorStore as ServiceStore;
      await scheduleGeneratorStore.create(schedulingParameters);
    } catch (err) {
      console.error("Error generating schedule:", err);
    }
  };

  toggleScheduleMode = () => {
    const routerStore = this.rootStore.routerStore;
    const currentMode = routerStore?.queryParams?.scheduleMode;
    const newMode = currentMode === "true" ? "false" : "true";
    routerStore?.goTo(null, null, { scheduleMode: newMode });
  };

  get entities() {
    const jobNumbers = Array.isArray(this.jobNumbers)
      ? toJS(this.jobNumbers)
      : [toJS(this.jobNumbers)];
    const jobNumbersAsNumbers = jobNumbers
      .map((jobNumber: any) => parseInt(jobNumber as string))
      .filter((jobNumber: any) => !isNaN(jobNumber));
    if (jobNumbersAsNumbers.length === 0) {
      console.log("No job numbers provided, returning empty entities array.");
      return [];
    }
    const panelStore = this.rootStore.panelStore as ServiceStore;
    if (!panelStore.entities) {
      console.log("PanelPlantStore entities not found, returning empty array.");
      return [];
    }
    const includedPanels = panelStore.entities.filter((entity: any) => {
      // if (entity.castingDate === "1970-01-01") return false;
      return jobNumbersAsNumbers.includes(entity.jobNumber as number);
    });
    return includedPanels;
  }

  get columnHeaders() {
    const titles = [1, 4, 7, 10, 12, 2, 5, 6, 8, 11, 13, 3, 9, 14];

    return [
      {
        width: 200,
        title: "Date",
        key: "castingDate",
        component: ScheduleInterfaceLeftHeaderCell,
        bodyComponent: ScheduleInterfaceLeftBodyCell,
      },
      ...titles.map((title: any) => ({
        width: 200,
        title: title.toString(),
        key: "jobNumber",
        headerKey: "jobNumber",
        component: ScheduleInterfaceRightHeaderCell,
        bodyComponent: ScheduleInterfaceRightBodyCell,
      })),
    ];
  }

  get aiScheduleDateRange() {
    const dateRange = getDateRange(this.startDate, this.endDate);
    console.log("aiScheduleDateRange", dateRange);
    return dateRange;
  }

  get cardHeight() {
    return this.rootStore.viewStore?.cardBodySize.height - 75;
  }

  get scheduleMode() {
    return this.rootStore.routerStore?.queryParams?.scheduleMode === "true";
  }

  get hasJobsSelected() {
    return (
      this.jobNumbers &&
      Array.isArray(this.jobNumbers) &&
      this.jobNumbers.length > 0 &&
      this.jobNumbers[0] !== null
    );
  }

  get readyToSchedule() {
    return (
      this.hasJobsSelected &&
      this.startDate &&
      this.endDate &&
      this.startDate !== "" &&
      this.endDate !== ""
    );
  }
}

export default AIAgentStore;
